<template>
  <!-- Client and partition switcher at the top of the sidebar (US 9372) -->
  <div class="ws-switcher" :class="{ 'ws-switcher--compact': compact }">
    <div class="ws-switcher__row">
      <button type="button"
              class="ws-card"
              :class="{ 'ws-card--open': open, 'ws-card--static': !canSwitch }"
              :disabled="!canSwitch"
              :aria-expanded="open ? 'true' : 'false'"
              :aria-label="$t('app_nav.switch_workspace')"
              @click="toggle"
              qid="workspace-switcher"
      >
        <WorkspaceAvatar :workspace="getClient" :size="36" class="ws-card__avatar"/>
        <span class="ws-card__text" v-if="!compact">
          <span class="ws-card__pre" v-if="getClient.parent">{{ getClient.parent.name }} ›</span>
          <span class="ws-card__name">{{ getClient.name }}</span>
          <span class="ws-card__sub" v-if="!getClient.parent && currentPartitions.length">
            {{ $t('app_nav.all_partitions') }} · {{ currentPartitions.length }}
          </span>
        </span>
        <font-awesome-icon icon="sort" class="ws-card__chevron" v-if="canSwitch && !compact"/>
      </button>

      <!-- Client or partition profile, next to the workspace it belongs to -->
      <router-link v-if="canSeeProfile && !compact"
                   :to="profileRoute"
                   class="ws-profile"
                   :aria-label="profileLabel"
                   v-b-tooltip.hover.right="profileLabel"
                   qid="workspace-profile-link"
      >
        <font-awesome-icon icon="id-card"/>
      </router-link>
    </div>

    <!-- The chip goes back to the client; its ✕ only hides the chip for this partition -->
    <div class="ws-partition-chip" v-if="getClient.parent && !compact && !chipHidden">
      <button type="button"
              class="ws-partition-chip__back"
              @click="switchTo(getClient.parent)"
              :title="$t('app_nav.back_to', { client: getClient.parent.name })"
              qid="workspace-back-to-client"
      >
        {{ $t('app_nav.partition') }} · {{ $t('app_nav.back_to', { client: getClient.parent.name }) }}
      </button>
      <button type="button"
              class="ws-partition-chip__hide"
              @click="hideChip"
              :title="$t('app_nav.hide')"
              :aria-label="$t('app_nav.hide')"
              qid="workspace-hide-partition-chip"
      >
        <font-awesome-icon icon="times"/>
      </button>
    </div>

    <div class="ws-backdrop" v-if="open" @click="close"></div>

    <div class="ws-pop"
         v-if="open"
         role="dialog"
         :aria-label="$t('app_nav.switch_workspace')"
         @keydown="onKeydown"
    >
      <div class="ws-pop__head">
        <span class="ws-pop__title">{{ $t('app_nav.switch_workspace') }}</span>
        <button type="button"
                class="ws-pop__close"
                @click="close"
                :aria-label="$t('app_nav.close')"
        >
          <font-awesome-icon icon="times"/>
        </button>
      </div>
      <div class="ws-search">
        <font-awesome-icon icon="search"/>
        <input ref="search"
               id="workspace-search"
               type="text"
               v-model="keyword"
               :placeholder="$t('app_nav.search_workspaces')"
               autocomplete="off"
               qid="workspace-search"
        >
        <span class="ws-kbd">Esc</span>
      </div>

      <div class="ws-list" ref="list">
        <template v-if="!keyword && recentRows.length">
          <div class="ws-label">{{ $t('app_nav.recent') }}</div>
          <button v-for="row in recentRows"
                  :key="'recent-' + row.key"
                  type="button"
                  class="ws-row"
                  :class="rowClass(row)"
                  @click="activate(row)"
                  @mouseenter="activeKey = row.key"
          >
            <WorkspaceAvatar :workspace="row.workspace" :size="22"/>
            <span class="ws-row__name">
              {{ row.workspace.name }}
              <span class="ws-row__meta" v-if="row.workspace.parent">· {{ row.workspace.parent.name }}</span>
            </span>
            <font-awesome-icon icon="check" class="ws-row__check" v-if="isCurrent(row.workspace)"/>
          </button>
          <div class="ws-sep"></div>
        </template>

        <div class="ws-label">{{ $t('app_nav.clients') }}</div>
        <template v-for="row in clientRows">
          <button :key="row.key"
                  type="button"
                  class="ws-row"
                  :class="rowClass(row)"
                  @click="activate(row)"
                  @mouseenter="activeKey = row.key"
          >
            <span class="ws-row__toggle"
                  v-if="row.type === 'client'"
                  role="button"
                  :aria-label="$t('app_nav.show_partitions')"
                  @click.stop="toggleClient(row.workspace)"
            >
              <font-awesome-icon :icon="isExpanded(row.workspace) ? 'angle-down' : 'angle-right'"/>
            </span>
            <WorkspaceAvatar :workspace="row.workspace" :size="row.type === 'client' ? 28 : 22" :partition="row.type === 'partition'"/>
            <span class="ws-row__name">
              <template v-if="row.type === 'client-level'">{{ $t('app_nav.all_of', { client: row.workspace.name }) }}</template>
              <template v-else>{{ row.workspace.name }}</template>
              <span class="ws-row__ref" v-if="row.type === 'client' && (row.workspace.cvr || row.workspace.reference_number)">
                <template v-if="row.workspace.cvr">CRN {{ row.workspace.cvr }}</template>
                <template v-if="row.workspace.cvr && row.workspace.reference_number"> · </template>
                <template v-if="row.workspace.reference_number">REF {{ row.workspace.reference_number }}</template>
              </span>
            </span>
            <font-awesome-icon icon="check" class="ws-row__check" v-if="row.type !== 'client' && isCurrent(row.workspace)"/>
          </button>
        </template>
        <div class="ws-empty" v-if="!clientRows.length && !loading">{{ $t('app_nav.no_workspaces') }}</div>
        <div class="ws-empty" v-if="loading && !clientRows.length">
          <font-awesome-icon icon="spinner" class="fa-spin"/>
        </div>
      </div>

      <div class="ws-foot">
        <span><span class="ws-kbd">↑↓</span> {{ $t('app_nav.hint_move') }}</span>
        <span><span class="ws-kbd">→</span> {{ $t('app_nav.hint_partitions') }}</span>
        <span><span class="ws-kbd">↵</span> {{ $t('app_nav.hint_switch') }}</span>
        <router-link v-if="canSeeProfile"
                     :to="profileRoute"
                     class="ws-foot__profile"
                     @click.native="close"
                     qid="workspace-popover-profile-link"
        >
          <font-awesome-icon icon="id-card"/> {{ profileLabel }}
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';
import WorkspaceAvatar from '@/components/navigation/WorkspaceAvatar.vue';
import permissionCheck from '@/util/ACL/permissionCheck';
import permissionMap from '@/util/ACL/permissionMap.js';

const RECENT_KEY = 'recentWorkspaces';
const RECENT_MAX = 3;
const HIDDEN_CHIPS_KEY = 'hiddenPartitionChips';

function readList(key) {
  try {
    let list = JSON.parse(localStorage.getItem(key) || '[]');
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

function readRecent() {
  return readList(RECENT_KEY);
}

export default {
  name: 'WorkspaceSwitcher',
  components: {
    WorkspaceAvatar
  },
  props: {
    // Icon rail: only the avatar is shown
    compact: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      open: false,
      keyword: '',
      loading: false,
      clients: [],
      partitionsByClient: {},
      expanded: {},
      activeKey: null,
      recent: readRecent(),
      // Partitions where the user hid the partition chip, kept in this browser only
      hiddenChips: readList(HIDDEN_CHIPS_KEY),
    }
  },
  computed: {
    getClient() {
      return this.$store.state.client
    },
    getUser() {
      return this.$store.state.user
    },
    rootClient() {
      return this.getClient.parent ? this.getClient.parent : this.getClient
    },
    // Same rule as the old user menu entry
    canSeeProfile() {
      return !!this.getClient.slug && permissionCheck(permissionMap.MAINTENANCE_VIEW);
    },
    profileRoute() {
      return { name: 'clientProfile', params: { slug: this.getClient.slug } };
    },
    chipHidden() {
      return this.hiddenChips.indexOf(this.getClient.slug) !== -1;
    },
    profileLabel() {
      return this.getClient.parent ? this.$t('top_nav.user_dropdown.partition_profile') : this.$t('top_nav.user_dropdown.client_profile');
    },
    currentPartitions() {
      return this.partitionsByClient[this.rootClient.slug] || []
    },
    // Nothing to switch to with one client and no partitions
    canSwitch() {
      let clientCount = this.getUser && this.getUser.clients ? this.getUser.clients.filter(c => !c.parent).length : 0;
      return clientCount > 1 || this.currentPartitions.length > 0 || !!this.getClient.parent || (this.getUser && this.getUser.isAdmin);
    },
    recentRows() {
      return this.recent.map(w => ({ key: 'r:' + w.slug, type: 'recent', workspace: w }));
    },
    // Clients with their partitions nested under them. With a keyword, a client is shown when its
    // name matches (server-side search) or one of its loaded partitions matches.
    clientRows() {
      let self = this;
      let keyword = this.keyword.trim().toLowerCase();
      let matches = name => !keyword || (name || '').toLowerCase().indexOf(keyword) !== -1;
      let clients = this.sortedClients;
      let rows = [];

      clients.forEach(function(client) {
        let partitions = self.partitionsByClient[client.slug] || [];
        let matchingPartitions = partitions.filter(p => matches(p.name));
        if (keyword && !matches(client.name) && !matchingPartitions.length) {
          return;
        }
        rows.push({ key: 'c:' + client.slug, type: 'client', workspace: client });
        if (self.isExpanded(client) || (keyword && matchingPartitions.length)) {
          rows.push({ key: 'l:' + client.slug, type: 'client-level', workspace: client });
          (keyword ? matchingPartitions : partitions).forEach(function(partition) {
            rows.push({ key: 'p:' + partition.slug, type: 'partition', workspace: partition });
          });
        }
      });
      return rows;
    },
    sortedClients() {
      let clients = this.clients.slice();
      if (!this.keyword && this.rootClient.slug && !clients.some(c => c.slug === this.rootClient.slug)) {
        clients.unshift(this.rootClient);
      }
      // The current client comes first
      return clients.sort((a, b) => (b.slug === this.rootClient.slug) - (a.slug === this.rootClient.slug));
    },
    navigableRows() {
      return (this.keyword ? [] : this.recentRows).concat(this.clientRows);
    }
  },
  watch: {
    getClient: function(value, oldValue) {
      if (value && value.slug && (!oldValue || value.slug !== oldValue.slug)) {
        this.loadPartitions(this.rootClient, true);
        this.rememberRecent(value);
      }
    },
    keyword: _.debounce(function() {
      this.loadClients();
    }, 300),
    open: function(value) {
      document.body.classList.toggle('ws-switcher-open', value);
    }
  },
  methods: {
    toggle() {
      if (this.open) {
        this.close();
        return;
      }
      this.open = true;
      this.keyword = '';
      this.activeKey = 'c:' + this.rootClient.slug;
      this.loadClients();
      this.$nextTick(() => this.$refs.search && this.$refs.search.focus());
    },
    close() {
      this.open = false;
    },
    isCurrent(workspace) {
      return workspace.slug === this.getClient.slug;
    },
    // The current client starts expanded until the user collapses it
    isExpanded(client) {
      if (Object.prototype.hasOwnProperty.call(this.expanded, client.slug)) {
        return this.expanded[client.slug];
      }
      return client.slug === this.rootClient.slug;
    },
    rowClass(row) {
      return {
        'ws-row--client': row.type === 'client',
        'ws-row--nested': row.type === 'client-level' || row.type === 'partition',
        'ws-row--current': row.type !== 'client' && this.isCurrent(row.workspace),
        'ws-row--active': row.key === this.activeKey
      };
    },
    toggleClient(client) {
      let expand = !this.isExpanded(client);
      this.$set(this.expanded, client.slug, expand);
      if (expand) {
        this.loadPartitions(client);
      }
    },
    activate(row) {
      if (row.type === 'client') {
        this.toggleClient(row.workspace);
        return;
      }
      this.switchTo(row.workspace);
    },
    onKeydown(event) {
      let rows = this.navigableRows;
      let index = rows.findIndex(r => r.key === this.activeKey);
      let row = rows[index];

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        let next = event.key === 'ArrowDown' ? Math.min(rows.length - 1, index + 1) : Math.max(0, index - 1);
        if (rows[next]) {
          this.activeKey = rows[next].key;
          this.scrollActiveIntoView();
        }
      } else if (event.key === 'ArrowRight' && row && row.type === 'client' && !this.isExpanded(row.workspace)) {
        event.preventDefault();
        this.toggleClient(row.workspace);
      } else if (event.key === 'ArrowLeft' && row && row.type === 'client' && this.isExpanded(row.workspace)) {
        event.preventDefault();
        this.toggleClient(row.workspace);
      } else if (event.key === 'Enter' && row) {
        event.preventDefault();
        this.activate(row);
      }
    },
    scrollActiveIntoView() {
      this.$nextTick(() => {
        let active = this.$refs.list && this.$refs.list.querySelector('.ws-row--active');
        if (active) {
          active.scrollIntoView({ block: 'nearest' });
        }
      });
    },
    loadClients() {
      let self = this;
      this.loading = true;
      piincHttp.get('clients', { params: {
        per_page: 100,
        without_partition: 1,
        member: !this.getUser.isAdmin ? this.getUser.id : undefined,
        keyword: this.keyword ? this.keyword : undefined
      }}).then(function(response) {
        self.clients = response.data.items;
        self.loading = false;
      }, function() {
        self.loading = false;
      });
    },
    loadPartitions(client, force) {
      let self = this;
      if (!client || !client.slug || (this.partitionsByClient[client.slug] && !force)) {
        return;
      }
      piincHttp.get('partitions', { params: {
        client: client.slug,
        per_page: 0,
        sort: 'name',
        order: 'asc',
        mine: !this.getUser.isAdmin ? 1 : undefined
      }}).then(function(response) {
        self.$set(self.partitionsByClient, client.slug, response.data.items || []);
      }, function() {});
    },
    // Same as the old top bar: load the user's permissions in the workspace, then store it.
    switchTo(workspace) {
      let self = this;
      this.close();
      if (this.isCurrent(workspace)) {
        return;
      }
      piincHttp.get('users/me/permissions/' + workspace.slug).then(function(response) {
        workspace.user_permissions = response.data.user_permissions;
        workspace.user_roles = response.data.user_roles;
        localStorage.setItem('clientObject', JSON.stringify(workspace));
        localStorage.setItem('client', workspace.slug);
        self.$store.commit('storeClient', workspace);
      }, function() {});
    },
    hideChip() {
      this.hiddenChips = readList(HIDDEN_CHIPS_KEY).filter(slug => slug !== this.getClient.slug).concat(this.getClient.slug);
      try {
        localStorage.setItem(HIDDEN_CHIPS_KEY, JSON.stringify(this.hiddenChips));
      } catch (e) {
        // Storage full or blocked: the chip stays hidden until the page reloads
      }
    },
    // The last workspaces the user was in, kept in this browser only
    rememberRecent(workspace) {
      let list = readRecent().filter(w => w.slug !== workspace.slug);
      list.unshift(workspace);
      this.recent = list.slice(0, RECENT_MAX);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(this.recent));
      } catch (e) {
        // Storage full or blocked: recent workspaces are only a convenience
      }
    }
  },
  created() {
    if (this.getClient && this.getClient.slug) {
      this.loadPartitions(this.rootClient);
      this.rememberRecent(this.getClient);
    }
  },
  beforeDestroy() {
    document.body.classList.remove('ws-switcher-open');
  }
}
</script>
